import api from "@/api";
import { defineStore } from "pinia";

export type InternalAdminAccountState = "active" | "inactive" | "invite-sent";

export interface InternalAdmin {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  designation: string;
  phoneNumber?: string;
  status: "active" | "inactive" | "archived";
  accountState: InternalAdminAccountState;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateInternalAdminPayload {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
}

export type InternalAdminSortKey = "name" | "email" | "status" | "createdAt" | "updatedAt";
export type InternalAdminSortDir = "asc" | "desc";

const deriveAccountState = (a: Partial<InternalAdmin> & { registrationToken?: string | null }): InternalAdminAccountState => {
  if (a.status === "active") {
    return "active";
  }
  if (a.status === "inactive" && a.registrationToken) {
    return "invite-sent";
  }
  return "inactive";
};

export const useInternalAdminStore = defineStore({
  id: "internalAdminStore",
  state: () => ({
    admins: [] as InternalAdmin[],
    loading: false,
    actionLoadingId: "" as string,
    sort: {
      key: "createdAt" as InternalAdminSortKey,
      dir: "desc" as InternalAdminSortDir,
    },
  }),
  getters: {
    sortedAdmins(state): InternalAdmin[] {
      const { key, dir } = state.sort;
      const multiplier = dir === "asc" ? 1 : -1;
      const copy = [...state.admins];
      copy.sort((a, b) => {
        let av: string | number = "";
        let bv: string | number = "";

        if (key === "name") {
          av = `${a.firstName || ""} ${a.lastName || ""}`.trim().toLowerCase();
          bv = `${b.firstName || ""} ${b.lastName || ""}`.trim().toLowerCase();
        } else if (key === "email") {
          av = (a.email || "").toLowerCase();
          bv = (b.email || "").toLowerCase();
        } else if (key === "status") {
          av = a.accountState;
          bv = b.accountState;
        } else if (key === "createdAt") {
          av = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bv = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        } else if (key === "updatedAt") {
          av = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          bv = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        }

        if (av < bv) return -1 * multiplier;
        if (av > bv) return 1 * multiplier;
        return 0;
      });
      return copy;
    },
  },
  actions: {
    setSort(key: InternalAdminSortKey) {
      if (this.sort.key === key) {
        this.sort.dir = this.sort.dir === "asc" ? "desc" : "asc";
      } else {
        this.sort.key = key;
        this.sort.dir = "asc";
      }
    },

    async fetch(): Promise<boolean> {
      this.loading = true;
      try {
        const res = await api.get("/v2/access/internal-admin/list");
        if (!res.data?.success) {
          throw new Error(res.data?.error || "Failed to fetch internal admins.");
        }
        this.admins = (res.data.data || []) as InternalAdmin[];
        return true;
      } catch (err) {
        console.error("Error fetching internal admins:", err);
        return false;
      } finally {
        this.loading = false;
      }
    },

    async create(payload: CreateInternalAdminPayload): Promise<{ success: boolean; error?: string }> {
      try {
        const res = await api.post("/v2/access/admin/initialise-new", {
          ...payload,
          designation: "Internal Admin (TECH)",
        });
        if (res.status === 201 && res.data?.success) {
          const saved = res.data.data;
          const newAdmin: InternalAdmin = {
            _id: saved._id,
            email: saved.email,
            firstName: saved.firstName,
            lastName: saved.lastName,
            designation: saved.designation,
            phoneNumber: saved.phoneNumber,
            status: saved.status,
            accountState: deriveAccountState({ ...saved, registrationToken: "pending" }),
            createdAt: saved.createdAt,
            updatedAt: saved.updatedAt,
          };
          this.admins = [newAdmin, ...this.admins];
          return { success: true };
        }
        return { success: false, error: res.data?.error || "Failed to create internal admin." };
      } catch (err: unknown) {
        const e = err as { response?: { data?: { error?: string } } };
        return { success: false, error: e?.response?.data?.error || "Failed to create internal admin." };
      }
    },

    async deactivate(id: string): Promise<boolean> {
      this.actionLoadingId = id;
      try {
        const res = await api.post(`/v2/access/internal-admin/deactivate/${id}`);
        if (res.data?.success) {
          this.replaceAdmin(res.data.data);
          return true;
        }
        return false;
      } catch (err) {
        console.error("Error deactivating internal admin:", err);
        return false;
      } finally {
        this.actionLoadingId = "";
      }
    },

    async activate(id: string): Promise<boolean> {
      this.actionLoadingId = id;
      try {
        const res = await api.post(`/v2/access/internal-admin/activate/${id}`);
        if (res.data?.success) {
          this.replaceAdmin(res.data.data);
          return true;
        }
        return false;
      } catch (err) {
        console.error("Error activating internal admin:", err);
        return false;
      } finally {
        this.actionLoadingId = "";
      }
    },

    async resendInvite(id: string): Promise<boolean> {
      this.actionLoadingId = id;
      try {
        const res = await api.post("/v2/access/admin/resend-invitation", { _id: id });
        return Boolean(res.data?.success);
      } catch (err) {
        console.error("Error resending invitation:", err);
        return false;
      } finally {
        this.actionLoadingId = "";
      }
    },

    async requestPasswordReset(email: string): Promise<boolean> {
      try {
        const res = await api.post("/v2/access/admin/admin-password-reset", { email });
        return Boolean(res.data?.success);
      } catch (err) {
        console.error("Error requesting password reset:", err);
        return false;
      }
    },

    async remove(id: string): Promise<boolean> {
      this.actionLoadingId = id;
      try {
        const res = await api.delete(`/v2/access/admin/delete/${id}`);
        if (res.data?.success) {
          this.admins = this.admins.filter((a) => a._id !== id);
          return true;
        }
        return false;
      } catch (err) {
        console.error("Error deleting internal admin:", err);
        return false;
      } finally {
        this.actionLoadingId = "";
      }
    },

    replaceAdmin(updated: InternalAdmin) {
      const idx = this.admins.findIndex((a) => a._id === updated._id);
      if (idx >= 0) {
        this.admins.splice(idx, 1, updated);
      }
    },
  },
});
